import type { DateField as DateFieldType } from '@/screens/ReportCreation/reportCreation.types';
import { FieldValues, useController } from 'react-hook-form';
import { YStack } from 'tamagui';
import { FieldError } from '../FieldError';
import type { ControlledFieldController } from '../formController.types';
import { DateField } from '@components/DateField/DateField';

const isDateValue = (value: unknown): value is Date => value instanceof Date;

interface ControlledDateFieldProps<FormValues extends FieldValues> extends Pick<
  DateFieldType,
  'label' | 'placeholder' | 'required'
> {
  formController: ControlledFieldController<FormValues>;
}

export const ControlledDateField = <TFieldValues extends FieldValues>({
  label,
  placeholder,
  required,
  formController,
}: ControlledDateFieldProps<TFieldValues>): React.ReactElement => {
  const { field: controllerField, fieldState } = useController<TFieldValues>({
    ...formController,
  });

  const incomingValue = controllerField.value;
  const normalizedValue = isDateValue(incomingValue)
    ? incomingValue
    : typeof incomingValue === 'string'
      ? new Date(incomingValue)
      : null;

  return (
    <YStack gap="$1">
      <DateField
        label={label}
        value={normalizedValue}
        onValueChange={controllerField.onChange}
        placeholder={placeholder}
        required={required}
      />
      <FieldError message={fieldState.error?.message} />
    </YStack>
  );
};
